import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import { apiFetch } from '../utils/auth';
import { buildApiUrl, API_CONFIG } from '../config';
import { 
  fullSync, 
  syncOperationsTypes, 
  syncAllOperationsTypes
} from '../utils/syncThrottled';
import { syncUsers } from '../services/nfc';
import GlobalSettingsModal from '../components/GlobalSettingsModal';
import DocumentationModal from '../components/DocumentationModal';
import UserIdentificationModal from '../components/UserIdentificationModal';
import { useTutorial } from '../contexts/TutorialContext';
import { canAccessSettings } from '../utils/permissions';
import { isTechnicalAccount, getRealUserIdentity, RealUserIdentity } from '../utils/realUser';

interface Einsatz {
  id: number;
  name: string;
  ort: string;
  typ: 'Ernsteinsatz' | 'Übung';
  remote?: boolean;
  categoryID?: number;
}

// Hilfsfunktion für Reverse-Geocoding (Nominatim)
async function getAddressFromCoords(lat: number, lon: number): Promise<string> {
  try {
    console.log('🌍 Reverse-Geocoding für Koordinaten:', lat, lon);
    // Verwende lokale API als Proxy für Nominatim über config und apiFetch
    const url = buildApiUrl(API_CONFIG.ENDPOINTS.GEOCODING_REVERSE(lat, lon));
    console.log('🌍 Geocoding-URL:', url);
    const response = await apiFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    
    if (response.ok) {
      const data = await response.json();
      console.log('🌍 Geocoding-Response:', data);
      const address = data.address || `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
      console.log('🌍 Ermittelte Adresse:', address);
      return address;
    } else {
      console.warn('⚠️ Geocoding-Response nicht OK:', response.status);
      // Fallback zu Koordinaten
      return `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
    }
  } catch (error) {
    console.warn('❌ Geocoding failed, using coordinates:', error);
    // Fallback zu Koordinaten
    return `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
  }
}

// Hilfsfunktion um zu prüfen, ob ein Einsatz Trupps zugewiesen hat
const hasTeams = (einsatz: any): boolean => {
  if (!einsatz.trupps || !Array.isArray(einsatz.trupps)) {
    return false;
  }
  
  // Prüfe ob der Einsatz mindestens einen Trupp hat
  return einsatz.trupps.length > 0;
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { startTutorial } = useTutorial();
  const tutorialStartedRef = useRef(false);
  const tutorialCheckedRef = useRef(false);
  const geoTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [einsaetze, setEinsaetze] = useState<Einsatz[]>(() => {
    const saved = localStorage.getItem('einsaetze');
    if (saved) return JSON.parse(saved);
    return [];
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [typ, setTyp] = useState<'Ernsteinsatz' | 'Übung' | null>(null);
  const [selectedCategoryID, setSelectedCategoryID] = useState<number | null>(null);
  const [ort, setOrt] = useState('');
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState('');
  // Globale Einstellungen
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [documentationOpen, setDocumentationOpen] = useState(false);
  const [buildInfo, setBuildInfo] = useState<any>(null);
  const [canAccessSettingsState, setCanAccessSettingsState] = useState(false);
  
  // Technischer Account: Benutzer-Identifikation
  const [showUserIdentification, setShowUserIdentification] = useState(false);
  const [pendingEinsatzType, setPendingEinsatzType] = useState<any>(null);

  // Load build info
  useEffect(() => {
    fetch('/build-info.json')
      .then(response => response.json())
      .then(data => {
        setBuildInfo(data);
        // Update localStorage with current version if not set
        const currentVersion = localStorage.getItem('currentAppVersion');
        if (!currentVersion) {
          localStorage.setItem('currentAppVersion', data.version);
        
        }
      })
      .catch(() => setBuildInfo({ version: 'unknown', buildTime: 'unknown' }));
  }, []);

  const resolveDisplayedVersion = (info: any): string => {
    const appVersion = typeof info?.appVersion === 'string' ? info.appVersion.trim() : '';
    if (appVersion) return appVersion;
    return 'unknown';
  };

  const resolveBuildId = (info: any): string => {
    const buildId = typeof info?.version === 'string' ? info.version.trim() : '';
    return buildId || 'unknown';
  };

  // Check settings permission
  useEffect(() => {
    const checkSettingsPermission = async () => {
      const hasPermission = await canAccessSettings();
      setCanAccessSettingsState(hasPermission);
    };
    
    checkSettingsPermission();
  }, []);

  // Cleanup für Geolocation-Timeout beim Unmount
  useEffect(() => {
    return () => {
      if (geoTimeoutRef.current) {
        clearTimeout(geoTimeoutRef.current);
        geoTimeoutRef.current = null;
      }
    };
  }, []);

  // Tutorial-Check beim Dashboard-Load (nur einmal)
  useEffect(() => {
    const checkTutorial = async () => {
      try {
        // Verhindere mehrfaches Starten des Tutorials
        if (tutorialStartedRef.current || tutorialCheckedRef.current) {
          console.log('🔄 Tutorial bereits gestartet oder geprüft - überspringe Check');
          return;
        }

        // Prüfe ob der Benutzer eingeloggt ist
        const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
        if (!accessToken) {
          console.log('⚠️ Kein Access-Token gefunden - Tutorial wird nicht gestartet');
          tutorialCheckedRef.current = true;
          return;
        }

        // Prüfe ob das Tutorial bereits abgeschlossen wurde
        const tutorialCompleted = localStorage.getItem('tutorialCompleted') === 'true';
        if (tutorialCompleted) {
          console.log('✅ Tutorial bereits abgeschlossen - wird nicht gestartet');
          tutorialCheckedRef.current = true;
          return;
        }

        // Prüfe zuerst den Flag aus der Login-Seite
        const tutorialNeeded = localStorage.getItem('tutorialNeeded');
        console.log('📊 Dashboard: Tutorial-Flag aus Login:', tutorialNeeded);
        
        if (tutorialNeeded === 'true') {
          console.log('🎯 Dashboard: Tutorial-Flag gesetzt - starte automatisch');
          console.log('🚀 Dashboard: Rufe startTutorial() auf...');
          tutorialStartedRef.current = true;
          tutorialCheckedRef.current = true;
          startTutorial();
          console.log('✅ Dashboard: startTutorial() wurde aufgerufen');
          // Flag löschen, damit es nicht nochmal startet
          localStorage.removeItem('tutorialNeeded');
          return;
        }

        // Fallback: Prüfe nochmal mit dem Tutorial-Manager
        const { getTutorialManager } = await import('../utils/tutorialManager');
        const tutorialManager = getTutorialManager();
        const needsTutorial = await tutorialManager.checkIfTutorialNeeded();
        
        if (needsTutorial) {
          console.log('🎯 Dashboard: Tutorial wird benötigt - starte automatisch');
          console.log('🚀 Dashboard: Rufe startTutorial() auf...');
          tutorialStartedRef.current = true;
          tutorialCheckedRef.current = true;
          startTutorial();
          console.log('✅ Dashboard: startTutorial() wurde aufgerufen');
        } else {
          tutorialCheckedRef.current = true;
        }
      } catch (error) {
        console.error('Fehler beim Tutorial-Check im Dashboard:', error);
        tutorialCheckedRef.current = true;
      }
    };

    // Tutorial-Check nur einmal nach kurzer Verzögerung
    const timer = setTimeout(() => {
      checkTutorial();
    }, 100);

    return () => clearTimeout(timer);
  }, []); // Leere Dependencies - läuft nur einmal beim Mount

  // Load initial data (ohne Tutorial-Logik)
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        await fullSync();
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };
    
    loadInitialData();
  }, []);


  const [remoteEinsaetze, setRemoteEinsaetze] = useState<any[]>(() => {
    const saved = localStorage.getItem('remoteEinsaetze');
    if (saved) return JSON.parse(saved);
    return [];
  });

  // Operations-Typen
  const [operationsTypes, setOperationsTypes] = useState<any[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('operationsTypes') || '[]');
    } catch {
      return [];
    }
  });

  // Alle verfügbaren Einsatztypen (für Einstellungen)
  const [allOperationsTypes, setAllOperationsTypes] = useState<any[]>([]);

  useEffect(() => {
    localStorage.setItem('einsaetze', JSON.stringify(einsaetze));
  }, [einsaetze]);

    const fetchActiveOperations = async () => {
      const accessToken = localStorage.getItem('accessToken');
      let orgID = null;
      if (accessToken) {
        try {
          const payload = JSON.parse(atob(accessToken.split('.')[1]));
          orgID = payload.orgID;
        } catch {}
      }
      if (!orgID) return;
      try {
      let response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.OPERATIONS_ACTIVE), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ orgID })
        });
        if (response.ok) {
          const data = await response.json();
          
        // Bestehende remoteEinsaetze aus localStorage laden
        const existingRemoteEinsaetze = JSON.parse(localStorage.getItem('remoteEinsaetze') || '[]');
        
        // Neue Daten von der API
        const newRemoteEinsaetze = Array.isArray(data) ? data : [];
        
        // Teams aus bestehenden Einsätzen beibehalten
        const mergedEinsaetze = newRemoteEinsaetze.map(newEinsatz => {
          // Suche nach bestehendem Einsatz mit gleicher ID
          const existingEinsatz = existingRemoteEinsaetze.find((existing: any) => 
            String(existing.id ?? existing.opsID ?? existing.name) === String(newEinsatz.id ?? newEinsatz.opsID ?? newEinsatz.name)
          );
          
          // Wenn ein bestehender Einsatz mit Teams gefunden wurde, diese beibehalten
          if (existingEinsatz && existingEinsatz.trupps && Array.isArray(existingEinsatz.trupps)) {
            return {
              ...newEinsatz,
              trupps: existingEinsatz.trupps
            };
          }
          
          // Ansonsten den neuen Einsatz ohne Teams zurückgeben
          return newEinsatz;
        });
        
        setRemoteEinsaetze(mergedEinsaetze);
        localStorage.setItem('remoteEinsaetze', JSON.stringify(mergedEinsaetze));
        } else {
          setRemoteEinsaetze([]);
          localStorage.setItem('remoteEinsaetze', '[]');
        }
      } catch (e) {
        setRemoteEinsaetze([]);
        localStorage.setItem('remoteEinsaetze', '[]');
      }
    };

  useEffect(() => {
    fetchActiveOperations();
    
    // Synchronisation beim Laden der Dashboard-Seite
    const syncData = async () => {
      try {
        const operationsTypes = await syncOperationsTypes();
        const allOpsTypes = await syncAllOperationsTypes();
        const users = await syncUsers(); // Benutzer synchronisieren
        
        if (operationsTypes) {
          setOperationsTypes(operationsTypes);
        }
        if (allOpsTypes) {
          setAllOperationsTypes(allOpsTypes);
        }
      } catch (error) {
        console.warn('Sync failed on dashboard load:', error);
      }
    };
    syncData();
  }, []);

  // Auth-Check entfernt - wird jetzt durch den Interceptor transparent behandelt
  // React.useEffect(() => {
  //   if (!isAuthenticated()) {
  //     navigate('/');
  //   }
  // }, [navigate]);

  // Schritt 1: Auswahl Einsatz-Typ (Legacy - wird nicht mehr verwendet)
  const handleSelectTyp = (selected: 'Ernsteinsatz' | 'Übung') => {

    // Diese Funktion wird nicht mehr verwendet
  };

  // Schritt 2: Ort bestätigen/bearbeiten (entfällt jetzt)
  const handleNextFromOrt = () => {
    setStep(3);
  };

  // Schritt 3: Speichern mit expliziten Werten
  // Funktion zum Aktualisieren des Standorts eines Einsatzes
  const updateOperationLocation = async (operationId: number, newLocation: string, typValue: 'Ernsteinsatz' | 'Übung', categoryIDValue: number, retryCount: number = 0) => {
    const maxRetries = 5; // Maximal 5 Versuche
    const retryDelay = 2000; // 2 Sekunden zwischen Versuchen
    
    console.log('🔄 updateOperationLocation aufgerufen:', { operationId, newLocation, retryCount });
    
    const accessToken = localStorage.getItem('accessToken');
    let orgID = null;
    
    if (accessToken) {
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        orgID = payload.orgID;
      } catch (error) {
        console.error('❌ Failed to parse token:', error);
        return;
      }
    } else {
      console.warn('⚠️ No access token found');
      return;
    }

    const selectedType = operationsTypes.find(t => t.id === categoryIDValue);
    const selectedTypeName = selectedType ? selectedType.name : '';

    try {
      if (orgID) {
        const requestBody = {
          orgID,
          title: `${newLocation}: ${selectedTypeName}`,
          alertTitle: selectedTypeName,
          location: newLocation,
          description: `${typValue} an ${newLocation}`,
          categoryID: categoryIDValue,
          category: selectedTypeName,
          type: categoryIDValue
        };

        console.log('📤 Sende Update-Request:', { 
          operationId, 
          orgID,
          url: API_CONFIG.ENDPOINTS.OPERATIONS_UPDATE(operationId), 
          requestBody,
          retryCount 
        });
        
        const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.OPERATIONS_UPDATE(operationId)), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });

        if (response.ok) {
          console.log('✅ Standort erfolgreich aktualisiert:', newLocation);
          // Remote-Liste aktualisieren, damit der neue Standort angezeigt wird
          await fetchActiveOperations();
          
          // Custom Event dispatchen, damit EinsatzDetail den Einsatz-State aktualisiert
          window.dispatchEvent(new CustomEvent('operationLocationUpdated', {
            detail: { operationId, newLocation }
          }));
        } else {
          const errorText = await response.text();
          console.warn('⚠️ Standort-Update fehlgeschlagen:', response.status, errorText);
          
          // Versuche es erneut, falls der Einsatz noch nicht vollständig erstellt wurde
          if (response.status === 404 && retryCount < maxRetries) {
            console.log(`⏳ Warte ${retryDelay/1000} Sekunden und versuche Update erneut (Versuch ${retryCount + 1}/${maxRetries})...`);
            setTimeout(async () => {
              await updateOperationLocation(operationId, newLocation, typValue, categoryIDValue, retryCount + 1);
            }, retryDelay);
          } else if (retryCount >= maxRetries) {
            console.error('❌ Standort-Update nach', maxRetries, 'Versuchen fehlgeschlagen. Einsatz möglicherweise nicht gefunden.');
          }
        }
      }
    } catch (error) {
      console.warn('⚠️ Fehler beim Aktualisieren des Standorts:', error);
      // Bei Netzwerkfehlern auch retry versuchen
      if (retryCount < maxRetries) {
        console.log(`⏳ Netzwerkfehler - Warte ${retryDelay/1000} Sekunden und versuche Update erneut (Versuch ${retryCount + 1}/${maxRetries})...`);
        setTimeout(async () => {
          await updateOperationLocation(operationId, newLocation, typValue, categoryIDValue, retryCount + 1);
        }, retryDelay);
      }
    }
  };

  const handleSaveWithValues = async (typValue: 'Ernsteinsatz' | 'Übung', ortValue: string, categoryIDValue: number, returnOperationId?: boolean): Promise<number | null> => {

    
    const accessToken = localStorage.getItem('accessToken');
    let orgID = null;
    let userID = null;
    
    if (accessToken) {
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        orgID = payload.orgID;
        userID = payload.id;
  
      } catch (error) {
        console.error('❌ Failed to parse token:', error);
      }
    } else {
      console.warn('⚠️ No access token found');
    }

    const selectedType = operationsTypes.find(t => t.id === categoryIDValue);
    const selectedTypeName = selectedType ? selectedType.name : '';

    try {
      // Versuche Remote hinzufügen (falls online)
      if (orgID && userID) {
  
        const requestBody = {
          orgID,
          title: `${ortValue}: ${selectedTypeName}`,
          alertTitle: selectedTypeName,
          location: ortValue,
          description: `${typValue} an ${ortValue}`,
          userID,
          categoryID: categoryIDValue || 1,
          category: selectedTypeName,
          type: categoryIDValue // type als categoryID (Zahl)
        };

        

        const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.OPERATIONS_ADD), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });

        if (response.ok) {
          const remoteOperation = await response.json();
          console.log('✅ Einsatz erstellt - Response:', remoteOperation);
          console.log('📋 Einsatz-ID:', remoteOperation.id, 'Typ:', typeof remoteOperation.id);
          console.log('📋 Einsatz orgID:', remoteOperation.orgID, 'Typ:', typeof remoteOperation.orgID);

          // Einsatz erfolgreich in DB gespeichert - nicht in lokale Liste hinzufügen
          // Der Einsatz wird beim nächsten Dashboard-Load als Remote-Einsatz geladen
          
          // Remote-Liste sofort aktualisieren
          await fetchActiveOperations();
          
          // Modal schließen und State zurücksetzen
          setModalOpen(false);
          setStep(1);
          setTyp(null);
          setOrt('');
          
          // Wenn returnOperationId true ist, nur die ID zurückgeben, sonst navigieren
          if (returnOperationId) {
            // Navigation wird vom Aufrufer übernommen
            setTimeout(() => navigate(`/einsatz/${remoteOperation.id}?new=true`), 100);
            return remoteOperation.id;
          }
          
          // Direkt zur Einsatz-Detail-Seite mit der DB-ID navigieren
          setTimeout(() => navigate(`/einsatz/${remoteOperation.id}?new=true`), 100);
          return remoteOperation.id;
        } else {
          const errorText = await response.text();
          console.error('❌ Failed to add operation remotely:', response.status, errorText);
          throw new Error(`Failed to add operation remotely: ${response.status} ${errorText}`);
        }
      } else {
        throw new Error('No orgID or userID available');
      }
    } catch (error) {
      console.error('❌ Error creating operation:', error);
      
      // Offline: nur lokal hinzufügen
      const newEinsatz: Einsatz = {
        id: Math.floor(Math.random() * 1000000) + 1000000,
        name: `${ortValue}: ${selectedTypeName}`,
        ort: ortValue,
        typ: typValue,
        categoryID: categoryIDValue || 1,
        remote: false,
      };
      
      setEinsaetze([...einsaetze, newEinsatz]);
      
      // Modal schließen und State zurücksetzen
      setModalOpen(false);
      setStep(1);
      setTyp(null);
      setOrt('');
      
      // Wenn returnOperationId true ist, nur die ID zurückgeben, sonst navigieren
      if (returnOperationId) {
        // Navigation wird vom Aufrufer übernommen
        setTimeout(() => navigate(`/einsatz/${newEinsatz.id}?new=true`), 100);
        return newEinsatz.id;
      }
      
      // Direkt zur Einsatz-Detail-Seite navigieren
      setTimeout(() => navigate(`/einsatz/${newEinsatz.id}?new=true`), 100);
      return newEinsatz.id;
    }
  };

  // Schritt 3: Speichern (Legacy - wird nicht mehr verwendet)
  const handleSave = async () => {

    // Diese Funktion wird nicht mehr verwendet, da handleSaveWithValues verwendet wird
  };

  const handleAbbrechen = () => {
    setModalOpen(false);
    setStep(1);
    setTyp(null);
    setSelectedCategoryID(null);
    setOrt('');
    setGeoError('');
    setGeoLoading(false);
  };

  // Nach Benutzer-Identifikation: Einsatz-Erstellung fortsetzen
  const handleUserIdentified = (identity: RealUserIdentity) => {
    setShowUserIdentification(false);
    const type = pendingEinsatzType;
    if (!type) return;
    setPendingEinsatzType(null);
    
    // Einsatz-Erstellung wie beim normalen Klick ausführen
    (async () => {
      try {
        console.log('🖱️ Einsatztyp geklickt (nach Identifikation):', type.name);
        console.log('👤 Identifiziert als:', identity.firstName, identity.lastName);
        
        const selectedTyp = type.name.includes('Übung') ? 'Übung' : 'Ernsteinsatz';
        const selectedCategoryIDValue = type.id;
        setSelectedCategoryID(selectedCategoryIDValue);
        setTyp(selectedTyp);
        setGeoLoading(true);
        setGeoError('');
        
        const fallbackOrt = 'Unbekannt';
        setOrt(fallbackOrt);
        
        let operationId: number | null = null;
        let locationResolved = false;
        let resolvedLocation: string | null = null;
        
        const triggerLocationUpdate = async (location: string) => {
          if (operationId !== null) {
            setTimeout(async () => {
              await updateOperationLocation(operationId!, location, selectedTyp, selectedCategoryIDValue);
            }, 2000);
          } else {
            resolvedLocation = location;
          }
        };
        
        if (navigator.geolocation) {
          if ('permissions' in navigator) {
            navigator.permissions.query({ name: 'geolocation' as PermissionName }).then(result => {
              if (result.state === 'denied') {
                setGeoError('Standort-Berechtigung wurde verweigert.');
              }
            }).catch(() => {});
          }
          
          geoTimeoutRef.current = setTimeout(() => {
            geoTimeoutRef.current = null;
            setGeoLoading(false);
            setGeoError('Standort konnte nicht innerhalb von 10 Sekunden ermittelt werden.');
          }, 10000);
          
          navigator.geolocation.getCurrentPosition(
            async pos => {
              if (geoTimeoutRef.current) {
                clearTimeout(geoTimeoutRef.current);
                geoTimeoutRef.current = null;
              }
              locationResolved = true;
              setGeoLoading(false);
              try {
                const address = await getAddressFromCoords(pos.coords.latitude, pos.coords.longitude);
                setOrt(address);
                resolvedLocation = address;
                await triggerLocationUpdate(address);
              } catch (e) {
                const coords = `${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}`;
                setOrt(coords);
                resolvedLocation = coords;
                await triggerLocationUpdate(coords);
              }
            },
            err => {
              if (geoTimeoutRef.current) {
                clearTimeout(geoTimeoutRef.current);
                geoTimeoutRef.current = null;
              }
              locationResolved = true;
              setGeoLoading(false);
              let errorMsg = 'Standort konnte nicht ermittelt werden.';
              if (err.code === 1) errorMsg = 'Standort-Berechtigung verweigert.';
              else if (err.code === 2) errorMsg = 'Standort nicht verfügbar.';
              else if (err.code === 3) errorMsg = 'Standortermittlung dauerte zu lange.';
              setGeoError(errorMsg);
            },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
          );
          await new Promise(resolve => setTimeout(resolve, 100));
        } else {
          setGeoLoading(false);
          setGeoError('Geodaten werden nicht unterstützt.');
        }
        
        operationId = await handleSaveWithValues(selectedTyp, fallbackOrt, selectedCategoryIDValue, true);
        if (!operationId) {
          setGeoLoading(false);
          setGeoError('Fehler beim Erstellen des Einsatzes.');
          return;
        }
        
        if (resolvedLocation && resolvedLocation !== fallbackOrt) {
          await triggerLocationUpdate(resolvedLocation);
        }
      } catch (error) {
        console.error('❌ Fehler beim Erstellen des Einsatzes:', error);
        setGeoLoading(false);
        setGeoError('Fehler beim Erstellen des Einsatzes.');
      }
    })();
  };

  const handleKachelClick = (einsatz: any) => {
    const id = einsatz.id ?? einsatz.opsID ?? einsatz.name;
    navigate(`/einsatz/${id}`);
  };

  // Filtere Einsätze basierend auf den ausgewählten Standard-Einsatztypen (Atemschutzeinsätze)
  // Warte bis operationsTypes geladen sind, bevor gefiltert wird
  const alleEinsaetze = React.useMemo(() => {
    const allEinsaetze = [
      ...remoteEinsaetze.map(e => ({ ...e, remote: true })),
      ...einsaetze.map(e => ({ ...e, remote: false }))
    ];

    // Wenn operationsTypes noch nicht geladen sind, zeige keine Einsätze (verhindert Flackern)
    if (!operationsTypes || operationsTypes.length === 0) {
      console.log('🔍 Dashboard: OperationsTypes noch nicht geladen - zeige keine Einsätze bis Filterung bereit ist');
      return [];
    }

    // Jetzt filtern basierend auf den geladenen operationsTypes
    const filteredEinsaetze = allEinsaetze.filter(einsatz => {
      // Prüfe ob der Einsatz-Typ in den Standard-Einsatztypen enthalten ist
      const einsatzTyp = einsatz.typ || einsatz.category || einsatz.categoryName;
      const istAtemschutzeinsatz = operationsTypes.some(opsType => 
        opsType.name === einsatzTyp || 
        opsType.id === einsatz.categoryID ||
        opsType.id === einsatz.categoryId
      );

      if (istAtemschutzeinsatz) {
        console.log('✅ Dashboard: Einsatz ist Atemschutzeinsatz:', einsatzTyp, 'für Einsatz:', einsatz.name || einsatz.title);
      } else {
        console.log('❌ Dashboard: Einsatz ist kein Atemschutzeinsatz:', einsatzTyp, 'für Einsatz:', einsatz.name || einsatz.title);
      }

      return istAtemschutzeinsatz;
    });

    console.log('🔍 Dashboard: Filterung abgeschlossen -', filteredEinsaetze.length, 'Einsätze gefiltert');
    return filteredEinsaetze;
  }, [remoteEinsaetze, einsaetze, operationsTypes]);

  return (
    <div className="dashboard-outer" style={{ padding: 32, marginTop: 64 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 className="einsatz-uebersicht-title" style={{ margin: 0 }}></h2>
        <div className="dashboard-header-actions" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => setDocumentationOpen(true)} title="Benutzerdokumentation" className="documentation-btn" style={{ background: '#c00', color: '#fff', border: 'none', borderRadius: 8, padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, width: 36, height: 36, boxShadow: '0 2px 8px #c002', cursor: 'pointer' }}><MenuBookIcon style={{ color: '#fff' }} /></button>
          {canAccessSettingsState && (
            <button onClick={() => setSettingsOpen(true)} title="Einstellungen" className="settings-btn" style={{ background: '#c00', color: '#fff', border: 'none', borderRadius: 8, padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, width: 36, height: 36, boxShadow: '0 2px 8px #c002', cursor: 'pointer' }}><SettingsIcon style={{ color: '#fff' }} /></button>
          )}
        </div>
      </div>
      <div className="einsatz-uebersicht-row" style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 24,
        justifyContent: 'flex-start',
        alignItems: 'stretch',
        width: '100%',
        margin: '0 auto',
        maxWidth: 1200,
      }}>
        <div
          style={{
            background: '#fafbfc',
            borderRadius: 20,
            padding: 18,
            minWidth: 300,
            maxWidth: 300,
            width: 300,
            minHeight: 120,
            textAlign: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 32px #0002',
            marginBottom: 24,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'box-shadow 0.2s',
            fontWeight: 700,
            fontSize: 20,
            flex: '1 1 300px'
          }}
          onClick={() => setModalOpen(true)}
        >
          <strong style={{ fontSize: 22, marginBottom: 12 }}>Neuer Einsatz</strong>
          <div style={{ margin: '12px 0' }}>
            <button style={{ fontSize: 32, borderRadius: '50%', width: 56, height: 56, background: '#c00', color: '#fff', border: 'none', boxShadow: '0 2px 8px #c002' }}>+</button>
          </div>
        </div>
        {alleEinsaetze.map(einsatz => (
          <div
            key={einsatz.id ?? einsatz.name}
            onClick={() => handleKachelClick(einsatz)}
            style={{
              cursor: 'pointer',
              position: 'relative',
              background: '#fafbfc',
              borderRadius: 20,
              padding: 18,
              minWidth: 300,
              maxWidth: 300,
              width: 300,
              minHeight: 120,
              textAlign: 'center',
              boxShadow: '0 4px 32px #0002',
              marginBottom: 24,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: '1 1 300px',
              transition: 'box-shadow 0.2s',
            }}
          >
            {einsatz.remote && (
              <CloudQueueIcon style={{ position: 'absolute', top: 16, right: 16, color: '#888', fontSize: 22 }} titleAccess="Remote-Einsatz" />
            )}
            {hasTeams(einsatz) && (
              <RadioButtonCheckedIcon 
                className="blinking-icon"
                style={{ 
                  position: 'absolute', 
                  top: 16, 
                  left: 16, 
                  color: '#ff0000', 
                  fontSize: 22 
                }} 
                titleAccess="Trupps zugewiesen" 
              />
            )}
            <strong style={{ fontSize: 22, marginBottom: 16, fontWeight: 900 }}>
              {einsatz.title ?? einsatz.name ?? 'Unbenannter Einsatz'}
            </strong>
            <div style={{ color: '#666', fontSize: 18, marginBottom: 4 }}>
              {einsatz.address ? `${einsatz.address}${einsatz.location ? ', ' + einsatz.location : ''}` : einsatz.location ?? einsatz.ort ?? ''}
            </div>
            <div style={{ color: '#bbb', fontSize: 16, marginTop: 2 }}>
              {einsatz.typ ?? einsatz.category ?? ''}
            </div>
          </div>
        ))}
      </div>
      {modalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div
            style={{
              background: '#fff',
              borderRadius: 16,
              padding: 20,
              minWidth: 450,
              minHeight: 300,
              maxWidth: 900,
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              boxShadow: '0 4px 32px #0002',
              width: 'auto',
            }}
          >
            {/* Titelbereich oben */}
            <div id="modal-title" style={{ width: '100%', textAlign: 'center', fontWeight: 700, fontSize: 22, marginBottom: 20, letterSpacing: 1 }}>
              Art des Einsatzes
            </div>
            {/* Mittlerer Bereich - dynamisch wachsend */}
            <div id="modal-middle" style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'none', flex: '1 1 auto' }}>
              <div style={{ pointerEvents: 'auto', width: '100%' }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: operationsTypes.length > 6 ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)',
                  gap: 18,
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '0 10px',
                  width: '100%'
                }}>
                  {operationsTypes.length > 0 ? (
                    operationsTypes.map((type) => (
                      <div
                        key={type.id}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('🖱️ CLICK ERKANNT - Einsatztyp:', type.name);
                          
                          // Prüfe ob technischer Account → Benutzer-Identifikation erforderlich
                          if (isTechnicalAccount()) {
                            const existingIdentity = getRealUserIdentity();
                            setPendingEinsatzType(type);
                            setShowUserIdentification(true);
                            return;
                          }
                          
                          (async () => {
                          try {
                            console.log('🖱️ Einsatztyp geklickt:', type.name);
                            
                            const selectedTyp = type.name.includes('Übung') ? 'Übung' : 'Ernsteinsatz';
                            const selectedCategoryIDValue = type.id;
                            setSelectedCategoryID(selectedCategoryIDValue);
                            setTyp(selectedTyp);
                            setGeoLoading(true);
                            setGeoError('');
                            
                            // Fallback-Standort für sofortige Erstellung
                            const fallbackOrt = 'Unbekannt';
                            setOrt(fallbackOrt);
                            
                            // WICHTIG: Standortermittlung SOFORT starten (vor dem Erstellen des Einsatzes),
                            // damit die Browser-Berechtigung noch im Benutzerinteraktions-Kontext angefordert wird
                            let operationId: number | null = null;
                            let locationResolved = false;
                            let resolvedLocation: string | null = null;
                            
                            // Funktion zum Aufrufen des Updates, wenn operationId verfügbar ist
                            const triggerLocationUpdate = async (location: string) => {
                              if (operationId !== null) {
                                console.log('📍 Triggering location update für operationId:', operationId, 'mit Standort:', location);
                                // Warte 2 Sekunden, damit der Einsatz vollständig in der DB erstellt wird
                                setTimeout(async () => {
                                  await updateOperationLocation(operationId!, location, selectedTyp, selectedCategoryIDValue);
                                }, 2000);
                              } else {
                                console.warn('⚠️ operationId noch nicht verfügbar, speichere Standort für später:', location);
                                resolvedLocation = location;
                              }
                            };
                            
                            // Starte Standortermittlung sofort (im Benutzerinteraktions-Kontext)
                            console.log('🔍 Prüfe navigator.geolocation:', !!navigator.geolocation);
                            if (navigator.geolocation) {
                            console.log('🌍 Starte Standortermittlung...');
                            
                            // Prüfe Berechtigungsstatus (falls verfügbar)
                            if ('permissions' in navigator) {
                              navigator.permissions.query({ name: 'geolocation' as PermissionName }).then(result => {
                                console.log('🔐 Geolocation-Berechtigung:', result.state);
                                if (result.state === 'denied') {
                                  setGeoError('Standort-Berechtigung wurde verweigert. Bitte in den Browser-Einstellungen aktivieren.');
                                }
                              }).catch(() => {
                                // Permissions API nicht unterstützt, ignorieren
                              });
                            }
                            
                            // Timeout von 10 Sekunden setzen
                            geoTimeoutRef.current = setTimeout(() => {
                              console.warn('⏱️ Standortermittlung-Timeout nach 10 Sekunden');
                              geoTimeoutRef.current = null;
                              setGeoLoading(false);
                              setGeoError('Standort konnte nicht innerhalb von 10 Sekunden ermittelt werden.');
                            }, 10000);
                            
                            // Standortermittlung starten (läuft im Hintergrund)
                            console.log('📍 Rufe getCurrentPosition auf...');
                            navigator.geolocation.getCurrentPosition(
                              async pos => {
                                console.log('✅ Position erfolgreich ermittelt:');
                                console.log('  - Latitude:', pos.coords.latitude);
                                console.log('  - Longitude:', pos.coords.longitude);
                                console.log('  - Accuracy:', pos.coords.accuracy, 'Meter');
                                console.log('  - Timestamp:', new Date(pos.timestamp).toISOString());
                                
                                // Timeout löschen, da Position erfolgreich ermittelt wurde
                                if (geoTimeoutRef.current) {
                                  clearTimeout(geoTimeoutRef.current);
                                  geoTimeoutRef.current = null;
                                }
                                
                                locationResolved = true;
                                setGeoLoading(false);
                                
                                try {
                                  console.log('🔄 Konvertiere Koordinaten zu Adresse...');
                                  console.log('  - Koordinaten:', pos.coords.latitude, pos.coords.longitude);
                                  const address = await getAddressFromCoords(pos.coords.latitude, pos.coords.longitude);
                                  console.log('✅ Adresse ermittelt:', address);
                                  console.log('  - Für Koordinaten:', pos.coords.latitude, pos.coords.longitude);
                                  setOrt(address);
                                  resolvedLocation = address;
                                  // Standort im Hintergrund aktualisieren
                                  await triggerLocationUpdate(address);
                                } catch (e) {
                                  console.error('❌ Fehler bei Adresskonvertierung:', e);
                                  const coords = `${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}`;
                                  setOrt(coords);
                                  resolvedLocation = coords;
                                  // Standort im Hintergrund aktualisieren
                                  await triggerLocationUpdate(coords);
                                }
                              },
                              err => {
                                console.error('❌ Standortermittlung fehlgeschlagen:', err.code, err.message);
                                
                                // Timeout löschen, da Fehler aufgetreten ist
                                if (geoTimeoutRef.current) {
                                  clearTimeout(geoTimeoutRef.current);
                                  geoTimeoutRef.current = null;
                                }
                                
                                locationResolved = true;
                                setGeoLoading(false);
                                
                                // Detaillierte Fehlermeldung
                                let errorMsg = 'Standort konnte nicht ermittelt werden.';
                                if (err.code === 1) {
                                  errorMsg = 'Standort-Berechtigung verweigert.';
                                } else if (err.code === 2) {
                                  errorMsg = 'Standort nicht verfügbar.';
                                } else if (err.code === 3) {
                                  errorMsg = 'Standortermittlung dauerte zu lange.';
                                }
                                setGeoError(errorMsg);
                              },
                              {
                                enableHighAccuracy: true,
                                timeout: 15000, // Erhöht auf 15 Sekunden für bessere Genauigkeit
                                maximumAge: 0 // Keine gecachten Positionen verwenden
                              }
                            );
                            
                            // Kurze Pause, damit Browser die Berechtigung anfordern kann
                            await new Promise(resolve => setTimeout(resolve, 100));
                          } else {
                            setGeoLoading(false);
                            setGeoError('Geodaten werden nicht unterstützt.');
                          }
                          
                          // Einsatz sofort erstellen (ohne auf Standort zu warten)
                          console.log('🚀 Erstelle Einsatz mit Fallback-Standort...');
                          operationId = await handleSaveWithValues(selectedTyp, fallbackOrt, selectedCategoryIDValue, true);
                          
                          if (!operationId) {
                            setGeoLoading(false);
                            setGeoError('Fehler beim Erstellen des Einsatzes.');
                            return;
                          }
                          
                          console.log('✅ Einsatz erstellt mit ID:', operationId);
                          
                          // Falls Standortermittlung bereits abgeschlossen ist, jetzt aktualisieren
                          // (falls sie sehr schnell war, bevor operationId gesetzt wurde)
                          if (resolvedLocation && resolvedLocation !== fallbackOrt) {
                            console.log('📍 Standort wurde bereits ermittelt, aktualisiere jetzt:', resolvedLocation);
                            await triggerLocationUpdate(resolvedLocation);
                          }
                          
                          // Standortermittlung läuft bereits im Hintergrund und wird den Einsatz aktualisieren, wenn fertig
                          } catch (error) {
                            console.error('❌ Fehler beim Erstellen des Einsatzes:', error);
                            setGeoLoading(false);
                            setGeoError('Fehler beim Erstellen des Einsatzes.');
                          }
                          })();
                        }}
                        style={{
                          background: '#c00',
                          color: '#fff',
                          borderRadius: 12,
                          padding: '18px 0',
                          cursor: 'pointer',
                          minWidth: 120,
                          textAlign: 'center',
                          fontWeight: 'bold',
                          fontSize: 18,
                          boxShadow: '0 2px 8px #c002',
                          transition: 'background 0.2s',
                          userSelect: 'none',
                        }}
                      >
                        {type.name}
                      </div>
                    ))
                  ) : (
                    <div style={{ textAlign: 'center', color: '#666', gridColumn: '1 / span 2' }}>
                      Lade Einsatz-Typen...
                    </div>
                  )}
                </div>
                {geoLoading && (
                  <div style={{ textAlign: 'center', color: '#666', marginTop: 16 }}>
                    Standort wird ermittelt...
                  </div>
                )}
                {geoError && (
                  <div style={{ textAlign: 'center', color: '#c00', marginTop: 16, fontSize: 14 }}>
                    {geoError}
                  </div>
                )}
              </div>
            </div>
            {/* Button-Bereich unten */}
            <div id="modal-bottom" style={{ width: '100%', display: 'flex', justifyContent: 'center', gap: 12, marginTop: 20 }}>
              <button
                onClick={handleAbbrechen}
                style={{
                  background: '#eee',
                  color: '#222',
                  border: 'none',
                  borderRadius: 12,
                  padding: '12px 24px',
                  fontWeight: 'bold',
                  fontSize: 15,
                  minWidth: 100,
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px #c002',
                }}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Globale Einstellungen Modal */}
      {canAccessSettingsState && (
        <GlobalSettingsModal 
          isOpen={settingsOpen} 
          onClose={() => setSettingsOpen(false)}
          orgID={(() => {
            try {
              const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
              if (accessToken) {
                const payload = JSON.parse(atob(accessToken.split('.')[1]));
                return payload.orgID || 0;
              }
            } catch (error) {
              console.error('Error extracting orgID from token:', error);
            }
            return 0;
          })()}
        />
      )}
      <style>{`
        @keyframes blink {
          0% { opacity: 1; }
          50% { opacity: 0.3; }
          100% { opacity: 1; }
        }
        
        .blinking-icon {
          animation: blink 2s infinite;
        }
        
        @media (max-width: 600px) {
          .dashboard-outer {
            padding: 0 16px !important;
            max-width: 100vw !important;
          }
          .einsatz-uebersicht-title {
            text-align: center !important;
            width: 100%;
            margin-top: 18px !important;
            margin-bottom: 0 !important;
          }
          .dashboard-header-actions {
            align-items: center !important;
            margin-top: 12px !important;
            margin-right: 4px !important;
          }
          .documentation-btn,
          .settings-btn {
            margin: 0 !important;
            width: 36px !important;
            height: 36px !important;
          }
          .einsatz-uebersicht-row {
            flex-direction: column !important;
            gap: 20px !important;
            align-items: center !important;
          }
          .einsatz-uebersicht-row > div {
            width: 86vw !important;
            max-width: 340px !important;
            min-width: unset !important;
            flex: none !important;
            margin: 0 auto !important;
            min-height: 120px !important;
            padding: 14px !important;
          }
          .einsatz-uebersicht-row > div:first-child {
            min-height: 120px !important;
            padding: 14px !important;
          }
        }
      `}</style>
      
      {/* Debug Menu */}
      <div style={{
        position: 'fixed',
        bottom: 8,
        right: 8,
        background: 'rgba(0,0,0,0.7)',
        color: '#fff',
        padding: '4px 8px',
        borderRadius: 4,
        fontSize: 11,
        fontFamily: 'monospace',
        zIndex: 1000,
        opacity: 0.8,
        cursor: 'pointer'
      }}
      onClick={() => {
        // Show build info in console (debug only)
        console.log('Build Info:', {
          appVersion: buildInfo?.appVersion || null,
          buildId: buildInfo?.version || 'unknown',
          buildDate: buildInfo?.buildDate || null,
          buildTime: buildInfo?.buildTime || null,
          timestamp: Date.now()
        });
      }}
      title="Klicke für Build-Info in der Konsole"
      >
        {resolveBuildId(buildInfo)} | v{resolveDisplayedVersion(buildInfo)}
      </div>
      
      {/* Modals */}
      <DocumentationModal
        isOpen={documentationOpen}
        onClose={() => setDocumentationOpen(false)}
      />

      {/* Benutzer-Identifikation für technische Accounts */}
      <UserIdentificationModal
        open={showUserIdentification}
        confirmMode={!!getRealUserIdentity()}
        onConfirm={handleUserIdentified}
        onCancel={() => {
          setShowUserIdentification(false);
          setPendingEinsatzType(null);
        }}
      />
      
    </div>
  );
};

export default Dashboard; 